import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import DataTableV2 from "common/components/DataTableV2";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import type { WorkspaceAccount } from "common/types";

import AccountActionsDropdown from "../AccountActionsDropdown";
import AccountName from "../AccountName";
import AccountProfileImage from "../AccountProfileImage";
import AccountProxyTag from "../AccountTags/AccountProxyTag";
import AccountSsiTag from "../AccountTags/AccountSsiTag";
import LicenseTag from "../AccountTags/LicenseTag";
import LoginStatusTag from "../AccountTags/LoginStatusTag";
import WarmupState from "../AccountTags/WarmupState";
import DesktopQuickAction from "./DesktopQuickAction";

import type { ColumnDef } from "@tanstack/react-table";

interface DesktopTableProps {
  accounts: WorkspaceAccount[];
  selectedAccounts: WorkspaceAccount[];
  setSelectedAccounts: (selectedAccounts: WorkspaceAccount[]) => void;
}

export default function DesktopTable({
  accounts,
  selectedAccounts,
  setSelectedAccounts,
}: DesktopTableProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions } = useSelectedWorkspaceContext();

  function onRowClick(account: WorkspaceAccount) {
    navigate({
      pathname: account.id,
      search: location.search, // This preserves all current query params
    });
  }

  const columns = useMemo(() => {
    const templateColumns: ColumnDef<WorkspaceAccount>[] = [
      {
        id: "account",
        header: "Account",
        cell: ({ row: { original } }) => {
          const { occupation } = original;

          return (
            <div className="flex items-center gap-x-2.5">
              <AccountProfileImage account={original} />

              <div className="flex flex-col gap-y-1 w-40">
                <AccountName account={original} className="text-md-bold" />

                <span className="line-clamp-1 break-all text-xs-medium text-black-500">
                  {occupation}
                </span>
              </div>
            </div>
          );
        },
        enableHiding: false,
      },
      {
        id: "ssi",
        header: "SSI",
        cell: ({ row: { original } }) => (
          <AccountSsiTag account={original} isTag={false} />
        ),
      },
      {
        id: "warmup",
        header: "Warmup",
        cell: ({ row: { original } }) => (
          <WarmupState accountId={original.id} isTag={false} />
        ),
      },
      {
        id: "proxy",
        header: "Proxy",
        cell: ({ row: { original } }) => <AccountProxyTag account={original} />,
      },
      {
        id: "seat",
        header: "Seat",
        cell: ({
          row: {
            original: { type, license },
          },
        }) => (
          <LicenseTag
            isRented={type === "rental"}
            license={license}
            size="sm"
          />
        ),
      },
      {
        id: "status",
        header: "Login status",
        cell: ({ row: { original } }) => (
          <LoginStatusTag account={original} size="sm" />
        ),
      },
      {
        id: "action",
        cell: ({ row: { original } }) => (
          <div className="inline-flex gap-x-2.5">
            <DesktopQuickAction account={original} />
            <AccountActionsDropdown account={original} />
          </div>
        ),
        enableHiding: false,
      },
    ];

    return templateColumns;
  }, []);

  return (
    <DataTableV2
      id="accounts"
      columns={columns}
      data={accounts}
      onRowClick={onRowClick}
      isLoading={false}
      onRowSelectionChange={
        permissions.includes("accounts_full") && setSelectedAccounts
      }
      selectedIds={selectedAccounts.map(({ id }) => id)}
    />
  );
}
